import { Component, Inject, Input, OnInit, OnDestroy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { DOCUMENT } from '@angular/common';
import {
  NG2_PDFJS_VIEWER_IFRAME,
  PdfAnnotation,
  AnnotationType,
} from './gk-document-preview-model';
import { GkDocumentPreviewService } from './gk-document-preview.service';
import { debounceTime, fromEvent, takeWhile } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'gk-document-preview',
  imports: [PdfJsViewerModule, TranslateModule],
  templateUrl: './gk-document-preview.component.html',
  styleUrl: './gk-document-preview.component.scss',
})
export class GkDocumentPreviewComponent implements OnInit, OnDestroy {
  @Input() annotationPreviewMode = false;
  private isAlive = true;
  pdfSrc: string | Blob | Uint8Array;
  imgSrc: string;
  textSrc: string;
  annotations: PdfAnnotation[];

  private iframe: HTMLIFrameElement | null = null;
  private iframeDoc: Document | null = null;

  constructor(
    @Inject(DOCUMENT) private dom: Document,
    private gkDocumentPreviewService: GkDocumentPreviewService,
  ) {}

  ngOnInit(): void {
    this.toggleLoading(true);
  }

  onDocumentLoad(): void {
    this.initIframe();
    this.renderAnnotations();
    this.initIframeListeners();
  }

  initIframe(): void {
    this.iframe = this.getIframe();
    this.iframeDoc = this.getIframeDocument();
    this.toggleLoading(false);
  }

  private getIframeDocument(): Document | null {
    return (
      this.iframe?.contentDocument ||
      this.iframe?.contentWindow?.document ||
      null
    );
  }

  private initIframeListeners(): void {
    if (!this.iframe || !this.iframeDoc) {
      return;
    }

    this.prepareIframe();
    this.registerResizeListener();
    this.registerScrollListener();
    this.registerZoomListener();
  }

  private prepareIframe(): void {
    this.hidePreviewToolbar();
    this.showIframe();
  }

  private registerResizeListener(): void {
    fromEvent(this.iframeDoc.defaultView, 'resize')
      .pipe(
        takeWhile(() => this.isAlive),
        debounceTime(1),
      )
      .subscribe(() => this.renderAnnotations());
  }

  private registerScrollListener(): void {
    fromEvent(this.iframeDoc, 'scroll').subscribe(() =>
      this.renderAnnotations(),
    );
  }

  private registerZoomListener(): void {
    fromEvent<WheelEvent>(this.iframeDoc, 'wheel')
      .pipe(
        takeWhile(() => this.isAlive),
        filter((event) => event.ctrlKey),
        debounceTime(1),
      )
      .subscribe(() => {
        this.renderAnnotations();
      });
  }

  hidePreviewToolbar(): void {
    const toolbar = this.iframeDoc.querySelector('#toolbarContainer');
    if (toolbar) {
      (toolbar as HTMLElement).style.display = 'none';
    }
  }

  private renderAnnotations(): void {
    if (!this.iframeDoc || !this.annotations?.length) {
      return;
    }

    this.gkDocumentPreviewService.removeAnnotations(this.iframeDoc);
    this.gkDocumentPreviewService.renderAnnotations(
      this.iframeDoc,
      this.annotations,
      this.onAnnotationClick.bind(this),
    );
  }

  onAnnotationClick(event: MouseEvent, annotation: PdfAnnotation): void {
    if (annotation.type == AnnotationType.Marker) {
      this.gkDocumentPreviewService.hideAllHighlights(this.iframeDoc);
      this.gkDocumentPreviewService.showMatchingHighlight(
        this.iframeDoc,
        annotation.id,
      );
    }

    if (annotation.type == AnnotationType.Highlight) {
      console.log('Clicked annotation:', annotation); // TODO
      console.log('Clicked event:', event); // TODO
    }
  }

  private getIframe(): HTMLIFrameElement | null {
    return this.dom.querySelector(NG2_PDFJS_VIEWER_IFRAME);
  }

  showIframe(): void {
    this.iframe.style.visibility = 'visible';
  }

  toggleLoading(visible: boolean): void {
    this.gkDocumentPreviewService.$loading.next(visible);
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
